// A section's changeable extra rows for this option (the template's rows
// 63–84): sundries, wash (take-off-wash.tsx), scaffold moves, hire (take-off-hire.tsx) and other
// trades. Each edit goes into the option's hig_scope; the figures come from
// pricing.ts. A material from the job's prices (#413) keeps its name and rate
// on the job, and every area using it follows; a typed line is this area's.
import { addAreaLine, jobMaterials, removeAreaLine, setScaffoldMoves, updateAreaLine, type AreaChoice, type OptionScope } from '@higgins/m2q-pricing';
import type { PricedSection, PricingRates, SectionInfo } from '@higgins/m2q-pricing';
import type { JobRowActions } from '../lib/job-prices';
import type { HireItemFromApi } from '../lib/types';
import { SavedTextField, TEXT_MAX } from './saved-text-field';
import { NumberField } from './number-field';
import { formatMoney, formatPercent } from './surface-format';
import { CHANGED } from './take-off-rows';
import { AddFromList, AddLine, JobPriceTag, Row, type Edit } from './take-off-extra-row';
import { small } from './take-off-small-number';
import { HireRows } from './take-off-hire';
import { SupplierQuoteControl } from './supplier-quote';
import { WashRow } from './take-off-wash';

/** A new sundries line's description, typed over by the estimator (#419). */
const NEW_SUNDRY = 'Sundry';
const NEW_TYPED = '__typed__';
const NEW_MATERIAL = '__new_material__';

interface Props {
  areaId: string;
  optionId: string;
  choice: AreaChoice | undefined;
  info: SectionInfo;
  /** Wash and Wash only as set on site. */
  siteWash: { wash: boolean; washOnly: boolean };
  priced: PricedSection;
  rates: PricingRates;
  hireItems: readonly HireItemFromApi[];
  job: JobRowActions;
  onScope: Edit;
}

/** The other-trade lines: typed for this area, or a material from the job's prices. */
function OtherTradeRows({ areaId, optionId, lines, rates, job, onScope }: { areaId: string; optionId: string; lines: NonNullable<AreaChoice['otherTrades']>; rates: PricingRates; job: JobRowActions; onScope: Edit }) {
  const markup = 1 + rates.markupOther;
  const materials = jobMaterials(job.prices);
  const addLine = (line: NonNullable<AreaChoice['otherTrades']>[number]) => onScope((s) => addAreaLine(s, areaId, 'otherTrades', line));

  function add(value: string) {
    if (value === NEW_TYPED) addLine({ description: 'Other trade / permit / material', qty: 1, rate: 0 });
    else if (value === NEW_MATERIAL) void job.onAddMaterial().then((jobPriceId) => addLine({ description: '', qty: 1, rate: 0, jobPriceId }));
    else addLine({ description: '', qty: 1, rate: 0, jobPriceId: value });
  }

  return (
    <>
      {lines.map((line, i) => {
        const set = (change: Record<string, unknown>) => onScope((s) => updateAreaLine(s, areaId, 'otherTrades', i, change));
        const material = line.jobPriceId ? materials.find((m) => m.id === line.jobPriceId) : undefined;
        const name = material?.name ?? line.description;
        const rate = material?.rate ?? line.rate;
        return (
          <Row key={`o${i}`} sell={line.qty * rate * markup} removal={{ question: `Remove ${name || 'this line'} from this area's price?`, remove: () => onScope((s) => removeAreaLine(s, areaId, 'otherTrades', i)) }}>
            <SavedTextField compact value={name} onSave={(text) => (material ? job.onRow(material.id, { hig_name: text }) : set({ description: text }))} label="Other trade" maxLength={TEXT_MAX} className="w-56" />
            {material && <JobPriceTag />}
            {small(line.qty, (v) => set({ qty: v ?? 0 }), 'Quantity')} ×
            {material ? (
              <NumberField compact value={material.rate} placeholder="0" onChange={(v) => job.onRow(material.id, { hig_rate: v ?? 0 })} label="Rate $ (every area on the job)" className={`w-14 ${CHANGED}`} />
            ) : (
              small(line.rate, (v) => set({ rate: v ?? 0 }), 'Rate $')
            )}{' '}
            $ <span className="text-xs text-slate-500">+ {formatPercent(rates.markupOther)}</span>
            <SupplierQuoteControl optionId={optionId} quote={line.quote} lineName={name} onQuote={(quote) => set({ quote })} />
          </Row>
        );
      })}
      <AddFromList label="Add other trade, permit or material" onPick={add}>
        <option value={NEW_TYPED}>Typed for this area</option>
        {materials.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name} (this job) — {formatMoney(m.rate ?? 0)}
          </option>
        ))}
        <option value={NEW_MATERIAL}>New job material (its rate applies to the whole job)</option>
      </AddFromList>
    </>
  );
}

export function TakeOffExtras({ areaId, optionId, choice, info, siteWash, priced, rates, hireItems, job, onScope }: Props) {
  const wage = rates.labour.sell + rates.allowance.sell;
  // Each change merges with the row as stored (the scope passed in), not as drawn: typing items then
  // minutes quickly must keep both.
  const movesIn = (s: OptionScope) => s.areas?.[areaId]?.scaffoldMoves ?? { count: 0, minutes: 0 };

  return (
    <>
      {(choice?.sundries ?? []).map((line, i) => {
        const set = (change: Record<string, unknown>) => onScope((s) => updateAreaLine(s, areaId, 'sundries', i, change));
        const figures = priced.sundries[i];
        return (
          <Row
            key={`s${i}`}
            hours={figures?.hours}
            litres={figures?.litres}
            sell={figures?.sell}
            removal={{ question: `Remove ${line.description || 'this sundry'} from this area's price?`, remove: () => onScope((s) => removeAreaLine(s, areaId, 'sundries', i)) }}
          >
            <SavedTextField compact value={line.description} onSave={(description) => set({ description })} label="Sundry" maxLength={TEXT_MAX} className="w-56" />
            {small(line.items, (v) => set({ items: v ?? 0 }), 'Sundry items')} items ×
            {small(line.minutes, (v) => set({ minutes: v ?? 0 }), 'Minutes each')} min, spread
            <NumberField compact value={line.spread ?? null} placeholder="1" onChange={(v) => set({ spread: v || null })} label="Sundry spread (hours per litre)" className="w-14" />
          </Row>
        );
      })}
      <AddLine label="Add sundry" onAdd={() => onScope((s) => addAreaLine(s, areaId, 'sundries', { description: NEW_SUNDRY, items: 0, minutes: 0 }))} />
      <WashRow areaId={areaId} choice={choice} info={info} site={siteWash} priced={priced} rates={rates} onScope={onScope} />
      <Row hours={priced.scaffoldHours} sell={priced.scaffoldHours * wage}>
        Scaffold moves {small(choice?.scaffoldMoves?.count, (v) => onScope((s) => setScaffoldMoves(s, areaId, { ...movesIn(s), count: v ?? 0 })), 'Scaffold moves')} ×
        {small(choice?.scaffoldMoves?.minutes, (v) => onScope((s) => setScaffoldMoves(s, areaId, { ...movesIn(s), minutes: v ?? 0 })), 'Minutes each')} min
      </Row>
      <HireRows areaId={areaId} optionId={optionId} lines={choice?.access ?? []} rates={rates} hireItems={hireItems} job={job} onScope={onScope} />
      <OtherTradeRows areaId={areaId} optionId={optionId} lines={choice?.otherTrades ?? []} rates={rates} job={job} onScope={onScope} />
    </>
  );
}
